import React, { useEffect } from 'react';
import AppShell from '../../../components/layout/AppShell';
import { Disclosure } from '@headlessui/react'
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/react/24/outline'
import {
  CloudArrowUpIcon,
  UserIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  MagnifyingGlassIcon,
  Square3Stack3DIcon,
  DocumentIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  BriefcaseIcon,
  CheckCircleIcon,
  SparklesIcon,
  HandThumbUpIcon,
  ArrowUpRightIcon
} from '@heroicons/react/24/outline';
import { Helmet } from 'react-helmet';
import { ArrowUpCircle } from 'lucide-react';

const faqs = [
  {
    id: 1,
    question: "How do I use CVHQ?",
    answer: "To use CVHQ, simply upload your existing CV and the job description. Our tool will do the rest, tailoring your CV to match the job requirements.",
  },
  {
    id: 2,
    question: "How do I cancel my subscription?",
    answer: "You can cancel your subscription by navigating to 'My Profile', selecting 'Manage Subscription', and following the cancellation instructions.",
  },
  {
    id: 3,
    question: "How much does CVHQ cost?",
    answer: "You can view our pricing plans on the Pricing page. Choose the plan that best suits your needs and get started today.",
  },
  {
    id: 4,
    question: "How do I update my details?",
    answer: "You can update your personal details such as Name, Organisation, and Email in the 'My Profile' section. Just navigate there to make any changes.",
  }
]

const featuresSummary = [
  {
    name: 'Resume Personalisation',
    description: 'Tailor your CV to specific job descriptions or RFQ documents. Upload the relevant documents, and our app will highlight the key skills and experiences that align with the employer\'s requirements.',
    icon: PencilSquareIcon,
    href: '#FeatureDetail_1',
  },
  {
    name: 'CV Creation from Text',
    description: 'Easily create a professional CV by uploading your existing resume or entering plain text. Our advanced algorithms will extract the relevant information and convert it into a standardised format.',
    icon: CloudArrowUpIcon,
    href: '#FeatureDetail_2',
  },
  {
    name: 'Maximise Efficiency',
    description: 'Revolutionise the way you create your CV with our ultra-fast tool. Convert, personalise, and correct your CV in less than 30 seconds, allowing you to apply for your dream job quicker than ever.',
    icon: UserIcon,
    href: '#FeatureDetail_3',
  },
  {
    name: 'Format Standardisation',
    description: 'Stand out from the crowd with a clean, elegant, and consistent CV format. Our app removes any inconsistencies and ensures your resume is visually appealing and easy to read.',
    icon: DocumentTextIcon,
    href: '#FeatureDetail_4',
  }
]

const featuresDetail_1 = [
  {
    name: 'Intelligent Keyword Analysis',
    description: 'Our advanced AI scans the job description or RFQ to identify crucial keywords and phrases. It then strategically incorporates these terms into your CV, ensuring optimal placement and frequency. This process not only helps your application pass through Applicant Tracking Systems (ATS) but also resonates with human recruiters, significantly increasing your chances of securing an interview.',
    icon: MagnifyingGlassIcon,
  },
  {
    name: 'Dynamic Content Customisation',
    description: 'Our tool intelligently tailors your entire CV content to the job description. It adapts your professional summary, reframes achievements to showcase their relevance, and prioritises your educational background based on the job\'s demands. CVHQ also applies industry-specific optimisation, adjusting terminology and formatting to match sector expectations, ensuring every element of your CV supports your application and speaks the language of your target industry.',
    icon: DocumentIcon,
  },
  {
    name: 'Comprehensive Requirement Mapping',
    description: 'CVHQ conducts a thorough analysis of the job\'s requirements, including hard skills, soft skills, and industry-specific competencies. It then meticulously aligns your experiences and qualifications with these requirements, reorganising your CV sections to emphasise the most relevant achievements. This creates a compelling narrative of your suitability for the role, tailored to each specific position.',
    icon: Square3Stack3DIcon,
  },
];

const featuresDetail_2 = [
  {
    name: 'Intelligent Information Extraction',
    description: 'Our sophisticated algorithms accurately identify and extract crucial information from your uploaded resume or plain text input, ensuring no important details are missed.',
    icon: ChatBubbleLeftRightIcon,
  },
  {
    name: 'Standardised Sections',
    description: 'The app organises your CV into clear, standardised sections such as Personal Details, Work Experience, Education, Skills, and more, making it easy for employers to find the information they need.',
    icon: EyeIcon,
  },
  {
    name: 'Multiple File Formats',
    description: 'Whether your existing resume is in PDF, Word, or plain text format, our app can handle it all. Simply upload your file, and let us take care of the rest.',
    icon: BriefcaseIcon,
  },
]

const featuresDetail_3 = [
  {
    name: 'Rapid Conversion',
    description: 'Our tool quickly converts your raw text or existing resume into a polished CV. This fast processing means you spend less time waiting and more time applying for jobs.',
    icon: ArrowUpRightIcon,
  },
  {
    name: 'Quick Personalisation',
    description: 'Instantly tailor your CV to specific job descriptions. Our app analyses and integrates key elements in seconds, ensuring your resume stands out immediately.',
    icon: EyeIcon,
  },
  {
    name: 'Error Correction',
    description: "Avoid the delays of manual proofreading. Our tool detects and corrects common mistakes in real time, ensuring your CV is error-free and ready to impress at a moment's notice.",
    icon: CheckCircleIcon,
  },
]

const featuresDetail_4 = [
  {
    name: 'Consistent Formatting',
    description: 'Our app ensures that your CV follows a consistent format throughout, with uniform fonts, font sizes, and spacing, creating a polished and professional look.',
    icon: SparklesIcon,
  },
  {
    name: 'Improved Readability',
    description: 'By standardising the format of your CV, we make it easier for employers to scan and find the information they need quickly, increasing your chances of being noticed.',
    icon: HandThumbUpIcon,
  },
  {
    name: 'ATS Compliance',
    description: 'Our standardised format is designed to be compatible with Applicant Tracking Systems (ATS), ensuring your CV is not overlooked due to formatting issues.',
    icon: ArrowUpRightIcon,
  },
]

const FeatureSection = ({ eyebrow, title, description, items }) => (
  <section className="public-card public-card-body">
    <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
      <div className="space-y-3">
        <h2 className="text-base font-semibold leading-7 text-primaryBlue">{eyebrow}</h2>
        <p className="text-3xl font-bold tracking-tight text-darkGrey sm:text-4xl">{title}</p>
        <p className="text-lg leading-8 text-slateGrey">{description}</p>
      </div>
      <dl className="grid gap-4 sm:grid-cols-2 xl:grid-cols-2">
        {items.map((feature) => (
          <div
            key={feature.name}
            className={
              feature.name === 'Comprehensive Requirement Mapping'
                ? 'public-secondary-surface p-5 sm:col-span-2'
                : 'public-secondary-surface p-5'
            }
          >
            <dt className="flex items-center gap-3 text-base font-semibold leading-7 text-darkGrey">
              <feature.icon className="public-icon-sm" aria-hidden="true" />
              {feature.name}
            </dt>
            <dd className="mt-3 text-sm leading-7 text-slateGrey">{feature.description}</dd>
          </div>
        ))}
      </dl>
    </div>
  </section>
)

const About = () => {
  useEffect(() => {
    const video = document.getElementById('demoVideo');
    const playButton = document.getElementById('playButton');
    const watchAgainButton = document.getElementById('watchAgainButton');
    const tryNowButton = document.getElementById('tryNowButton');
    const overlay = document.getElementById('overlay');

    if (!video || !playButton || !watchAgainButton || !tryNowButton || !overlay) {
      return undefined;
    }

    const showOverlay = () => {
      overlay.classList.remove('hidden');
      playButton.style.display = 'flex';
      watchAgainButton.style.display = 'none';
      tryNowButton.style.display = 'none';
    };

    const showEndOverlay = () => {
      overlay.classList.remove('hidden');
      playButton.style.display = 'none';
      watchAgainButton.style.display = 'flex';
      tryNowButton.style.display = 'flex';
    };

    const hideOverlay = () => {
      overlay.classList.add('hidden');
      playButton.style.display = 'none';
    };

    const handlePlay = () => hideOverlay();
    const handlePause = () => showOverlay();
    const handleEnded = () => showEndOverlay();
    const handlePlayButtonClick = () => {
      video.play();
      hideOverlay();
    };
    const handleWatchAgainClick = () => {
      video.currentTime = 0;
      video.play();
      hideOverlay();
    };
    const handleTryNowClick = () => {
      window.location.href = '/register';
    };

    showOverlay();

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    playButton.addEventListener('click', handlePlayButtonClick);
    watchAgainButton.addEventListener('click', handleWatchAgainClick);
    tryNowButton.addEventListener('click', handleTryNowClick);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      playButton.removeEventListener('click', handlePlayButtonClick);
      watchAgainButton.removeEventListener('click', handleWatchAgainClick);
      tryNowButton.removeEventListener('click', handleTryNowClick);
    };
  }, []);

  return (
    <AppShell currentPage="About" loginRequired={false}>
      <Helmet>
        <title>CVHQ | About | CV Converter - AI-Powered Resume Tool</title>
        <meta
          name="description"
          content="Learn more about our free AI-powered CV Converter. Our innovative tool helps job seekers create professional, tailored resumes in seconds. Discover how we can revolutionise your job search and increase your chances of landing your dream job."
        />
        <meta
          name="keywords"
          content="about CV converter, about resume converter, free CV converter, free resume converter, AI-powered CV converter, AI-driven resume conversion, convert CV online, convert resume online, tailor CV, tailor resume, customize CV, customize resume, optimize CV, optimize resume, professional CV, professional resume, job application, career boost, increase job prospects, land dream job"
        />
      </Helmet>

      {/* Header */}

      {/* Page-specific content */}


      {/* Feature Summary */}
      <main>
        <div className="public-page">
          <div id="top" className="public-page-shell public-section">
            <div className="space-y-16">
              <div className="public-page-hero py-0">
                <h1 className="public-page-title">Effortless CV Conversion and Optimisation</h1>
                <p className="public-page-intro">
                  Transform your CV into a professional, tailored document that showcases your skills and experience. Our powerful tools simplify the process of creating a standout CV, saving you time and effort.
                </p>
              </div>

              <div className="application-panel relative overflow-hidden">
                <video
                  id="demoVideo"
                  src="/CVHQ Demo Video.mp4"
                  className="h-auto w-full rounded-md"
                  muted
                  poster="/CVHQDemoVideoPoster.png"
                >
                  Your browser does not support the video tag.
                </video>
                <div
                  id="overlay"
                  className="absolute inset-0 hidden flex items-center justify-center bg-slate-950/50 transition-opacity duration-300"
                >
                  <button
                    id="playButton"
                    className="bg-white px-6 py-3 text-lg font-semibold text-darkGrey shadow-sm transition-colors hover:bg-slate-100 max-w-xs w-full rounded-md sm:w-auto"
                  >
                    Discover the 3-Step CV Conversion Process
                  </button>
                  <button
                    id="watchAgainButton"
                    className="hidden bg-white px-6 py-3 text-lg font-semibold text-darkGrey shadow-sm transition-colors hover:bg-slate-100 mx-2 max-w-xs w-full rounded-md sm:w-auto"
                  >
                    Watch Again
                  </button>
                  <button
                    id="tryNowButton"
                    className="hidden bg-primaryBlue px-6 py-3 text-lg font-semibold text-white shadow-sm transition-colors hover:bg-deepNavy mx-2 max-w-xs w-full rounded-md sm:w-auto"
                  >
                    Try Now
                  </button>
                </div>
              </div>

              <div className="mx-auto max-w-3xl text-center">
                <p className="public-section-title sm:text-4xl">Discover the Power of CVHQ</p>
                <p className="mt-4 public-body-lg">
                  Explore our features and see how AI-driven customisation can elevate your job application process.
                </p>
              </div>

              <div className="mx-auto max-w-4xl">
                <dl className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {featuresSummary.map((feature) => (
                    <div key={feature.name} className="public-card public-card-body h-full">
                      <div className="flex items-start gap-4">
                        <feature.icon className="public-icon-sm mt-1" aria-hidden="true" />
                        <div className="flex-1">
                          <dt className="text-base font-semibold leading-7 text-darkGrey">{feature.name}</dt>
                          <dd className="mt-2 text-base leading-7 text-slateGrey">{feature.description}</dd>
                          <p className="mt-6">
                            <a href={feature.href} className="public-link">
                              Learn more
                            </a>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>

          <div id="FeatureDetail_1" className="public-page-shell public-section">
            <FeatureSection
              eyebrow="Automate Your Success"
              title="Personalised Tailoring"
              description="Optimise your resume effortlessly by uploading an RFQ or Job Description. Our intelligent system analyses the content, identifies essential keywords and requirements, and tailors your CV to increase your chances of landing the job."
              items={featuresDetail_1}
            />
          </div>

          <div id="FeatureDetail_2" className="public-page-shell public-section">
            <FeatureSection
              eyebrow="Streamline Your Application"
              title="CV Creation from Text"
              description="Transform your existing resume or plain text into a professional CV effortlessly. Upload your document, and our advanced algorithms will extract and organise the relevant information into a standardised, elegant format."
              items={featuresDetail_2}
            />
          </div>

          <div id="FeatureDetail_3" className="public-page-shell public-section">
            <FeatureSection
              eyebrow="Maximise Efficiency"
              title="Swift CV Processing"
              description="Empower your job application process with our rapid CV tool. Convert, personalise, and correct your CV in under 30 seconds, freeing up more time for the important things."
              items={featuresDetail_3}
            />
          </div>

          <div id="FeatureDetail_4" className="public-page-shell public-section">
            <FeatureSection
              eyebrow="Perfect Your Presentation"
              title="Format Standardisation"
              description="Achieve a polished and professional look with our format standardisation feature. Our app ensures your CV follows a consistent format, making it more appealing and easier to read for employers."
              items={featuresDetail_4}
            />
          </div>

          <div className="public-page-shell pb-12 text-center">
            <a href="#top" className="public-link inline-flex items-center">
              <ArrowUpCircle className="w-5 h-5 mr-2" />
              Back to top
            </a>
          </div>

          <div className="public-page-shell public-section pt-0">
            <div className="public-card public-card-body mx-auto max-w-4xl">
              <h2 className="text-2xl font-bold leading-10 tracking-tight text-darkGrey">Frequently asked questions</h2>
              <dl className="mt-10 space-y-6 divide-y divide-slate-200">
                {faqs.map((faq) => (
                  <Disclosure as="div" key={faq.question} className="pt-6">
                    {({ open }) => (
                      <>
                        <Disclosure.Button className="flex w-full flex-col items-start justify-between text-left text-darkGrey">
                          <div className="flex w-full items-center justify-between">
                            <span className="text-base font-semibold leading-7">{faq.question}</span>
                            <span className="ml-6 flex h-7 items-center">
                              {open ? (
                                <MinusSmallIcon className="h-6 w-6" aria-hidden="true" />
                              ) : (
                                <PlusSmallIcon className="h-6 w-6" aria-hidden="true" />
                              )}
                            </span>
                          </div>
                          <Disclosure.Panel as="dd" className="mt-2 w-full pr-12">
                            <p className="text-base leading-7 text-slateGrey">{faq.answer}</p>
                          </Disclosure.Panel>
                        </Disclosure.Button>
                      </>
                    )}
                  </Disclosure>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </main>
    </AppShell>
  );
};

export default About;


